// src/pages/modules/Accounting/DebtManager/DebtManager.tsx
// จัดการหนี้สิน (data_c) — ปรับดอก / ล้มยอด / หนี้เสีย / จัดการงวด

import React from 'react'
import {
  Alert, Badge, Button, Card, Col, Form, InputGroup,
  Modal, Row, Spinner, Table,
} from 'react-bootstrap'
import { AuthContext, dbRead, dbUpdate, dbSet, dbRemove } from '../../../../_services/firebase'
import { DB_SLIP_BY_ITEM_ID, DB_COLLECT, DB_COLLECT_BY_ID, DB_COLLECT_IMG_ITEM } from '../../../../_config/db_paths'
import { getAllCustomers, markBadDebt, PATH } from '../../../../_services/customer.service'
import { saveLog } from '../../../../_services/firebase'
import {
  formatCurrency, formatDate, formatDateShort, toastDanger, toastSuccess,
  calcLoan, genCustomerId,
} from '../../../../_utils'
import ConfirmAlert from '../../../../components/ConfirmAlert/ConfirmAlert'
import { generateDebtSheet, generateCollectionSummary } from '../../../../_utils/generateDebtSheet'
import type { DebtSheetRow, CollectionRow } from '../../../../_utils/generateDebtSheet'
import type { CustomerReg, LoanData, InstallmentItem, InstallmentStatus } from '../../../../_types'

// ─── Helpers ──────────────────────────────────────────────────
const money = formatCurrency
const round = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

function calcRemain(loan: LoanData, installments: InstallmentItem[]): number {
  // ยอดคงเหลือ = ยอดรวมทั้งสัญญา − ยอดที่จ่ายมาแล้ว (รวมงวด paid + partial)
  const paid = installments.filter((i) => i.status === 'paid' || i.status === 'partial')
    .reduce((s, i) => s + i.paidAmount, 0)
  return Math.max(0, loan.totalWithInterest - paid)
}

function paidCount(installments: InstallmentItem[]): number {
  return installments.filter((i) => i.status === 'paid').length
}

// งวดถัดไปสำหรับ "เพิ่มงวดเกิน" (เช่น 60 งวดเต็มแล้วแต่ยอดยังไม่ครบ → งวด 61)
function nextExtraSeq(installments: InstallmentItem[]): number {
  return installments.reduce((mx, i) => Math.max(mx, i.seq), 0) + 1
}

const DAY_MS = 86400000

// วันครบกำหนดของงวด: ใช้ dueAt ถ้ามี ไม่งั้นคำนวณจากวันเริ่มสัญญา (จาก key) + (งวด−1) วัน
function dueMsOf(ins: InstallmentItem, customerKey: string): number {
  const at = (ins as any).dueAt
  if (at) return at
  const startMs = Number(String(customerKey).split('_')[1]) || 0
  return startMs ? startMs + (ins.seq - 1) * DAY_MS : 0
}

// epoch ms → 'yyyy-MM-dd' (เวลาไทย) สำหรับ <input type='date'>
// epoch ms → 'yyyy-MM-dd' (เวลาไทย) สำหรับ <input type='date'>
// ไม่ใช้ toLocaleDateString เพราะบางเบราว์เซอร์แทรกอักขระซ่อน ทำให้ input ไม่รับค่า
function toDateInput(ms: number): string {
  if (!ms) return ''
  return new Date(ms + 7 * 3600000).toISOString().slice(0, 10)   // +7 ชม. = เวลาไทย
}

// ─── SummaryTable ─────────────────────────────────────────────
function SummaryTable({ title, rows }: { title: string; rows: { label: string; value: string; highlight?: boolean }[] }) {
  return (
    <Card className='shadow-sm border-0' style={{ backgroundColor: '#fafafa' }}>
      <Card.Body className='p-2'>
        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: '#555' }}>{title}</div>
        <Table size='sm' style={{ fontSize: 12, marginBottom: 0 }}>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={{ color: r.highlight ? '#c62828' : '#666', fontWeight: r.highlight ? 700 : 400 }}>{r.label}</td>
                <td style={{ textAlign: 'right', fontWeight: r.highlight ? 700 : 400, color: r.highlight ? '#c62828' : undefined }}>
                  {r.value}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  )
}

// ─── Field Collection (รับเงินภาคสนาม) ────────────────────────
const FIELD_WINDOW_MS = 2 * 86400000   // ย้อนหลังไม่เกิน 2 วัน

interface FieldReceipt {
  id: string; amount: number; hasImage?: boolean; note?: string
  collectedBy: string; collectedAt: number; status?: string
}

function ReceiptRow({ receipt, installments, customerKey, remain, saving, onApply }: {
  receipt: FieldReceipt
  installments: InstallmentItem[]
  customerKey: string
  remain: number
  saving: boolean
  onApply: (seq: number, amount: number) => void
}) {
  const firstUnpaid = installments.find((i) => i.status !== 'paid')?.seq ?? (installments[0]?.seq ?? 1)
  const [seq, setSeq]       = React.useState<number>(firstUnpaid)
  const [amount, setAmount] = React.useState<string>(String(receipt.amount || ''))
  const [zoom, setZoom]     = React.useState(false)
  const [img, setImg]       = React.useState('')
  React.useEffect(() => {
    setSeq(firstUnpaid); setAmount(String(receipt.amount || '')); setImg('')
    if (receipt.hasImage) {
      dbRead<{ image?: string }>(DB_COLLECT_IMG_ITEM(customerKey, receipt.id))
        .then((d) => { if (d?.image) setImg(d.image) }).catch(() => {})
    }
  }, [receipt.id])

  return (
    <div className='mb-3 p-2' style={{ background: '#f7f7f7', borderRadius: 6 }}>
      <div className='d-flex align-items-center gap-2 mb-2'>
        {receipt.hasImage
          ? (img
              ? <img src={img} alt='' onClick={() => setZoom(true)}
                  style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} />
              : <div style={{ width: 48, height: 48, borderRadius: 4, background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner size='sm' animation='border' /></div>)
          : <div style={{ width: 48, height: 48, borderRadius: 4, background: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className='fa-solid fa-money-bill' /></div>}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: '#2e7d32' }}>รับมา {money(receipt.amount)} กีบ</div>
          <div style={{ fontSize: 10, color: '#888' }}>{formatDate(receipt.collectedAt)}{receipt.note ? ` · ${receipt.note}` : ''}</div>
        </div>
      </div>
      <Row className='g-2'>
        <Col xs={7}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>ลงงวด</Form.Label>
          <Form.Select size='sm' value={seq} onChange={(e) => setSeq(Number(e.target.value))}>
            {installments.map((ins) => {
              const ms = dueMsOf(ins, customerKey)
              return (
                <option key={ins.seq} value={ins.seq}>
                  งวด {ins.seq}{ms ? ` · ${formatDateShort(ms)}` : ''} — {money(ins.dueAmount)} ({ins.status})
                </option>
              )
            })}
            {remain > 0 && (() => {
              const ns = nextExtraSeq(installments)
              return <option value={ns}>➕ เพิ่มงวด ({ns}) — ค้าง {money(remain)}</option>
            })()}
          </Form.Select>
        </Col>
        <Col xs={5}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>ยอดที่ลง</Form.Label>
          <Form.Control
            size='sm' type='text' inputMode='numeric'
            value={amount ? Number(amount).toLocaleString('en-US') : ''}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
          />
        </Col>
      </Row>
      <Button size='sm' variant='success' className='w-100 mt-2' disabled={saving}
        onClick={() => onApply(seq, parseFloat(amount) || 0)}>
        {saving ? <Spinner size='sm' animation='border' /> : <><i className='fa-solid fa-check me-1' />ลงงวด {seq} &amp; ยืนยัน</>}
      </Button>
      {zoom && img && (
        <div onClick={() => setZoom(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={img} alt='' style={{ maxWidth: '92%', maxHeight: '92%' }} />
        </div>
      )}
    </div>
  )
}

function ReviewFieldModal({ show, reg, receipts, installments, remain, saving, onApply, onClose }: {
  show: boolean; reg: CustomerReg | null; receipts: FieldReceipt[]
  installments: InstallmentItem[]; remain: number; saving: boolean
  onApply: (receipt: FieldReceipt, seq: number, amount: number) => void
  onClose: () => void
}) {
  if (!reg) return null
  const total = receipts.reduce((s, r) => s + (r.amount || 0), 0)
  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 15 }}>
          <i className='fa-solid fa-camera me-2' />ตรวจรับเงินภาคสนาม — {reg.personal.fullName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 12 }}>
        <Alert variant='info' style={{ fontSize: 11 }}>
          เลือกงวดที่จะลง + แก้ยอดได้ (ค่าเริ่มต้น = ยอดที่รับมาจริง) เมื่อกด "ลงงวด &amp; ยืนยัน" ระบบจะบันทึกเข้าบัญชีงวดนั้นและซ่อนรายการนี้
        </Alert>
        {receipts.length === 0 && <div className='text-muted'>ไม่มีรายการรอยืนยัน</div>}
        {receipts.map((r) => (
          <ReceiptRow key={r.id} receipt={r} installments={installments} customerKey={reg.key}
            remain={remain} saving={saving} onApply={(seq, amount) => onApply(r, seq, amount)} />
        ))}
        {receipts.length > 0 && (
          <div className='d-flex justify-content-between align-items-center mt-2' style={{ fontWeight: 700 }}>
            <span>รวมที่รับมา (ยังไม่ยืนยัน)</span><span style={{ color: '#2e7d32' }}>{money(total)} กีบ</span>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' size='sm' onClick={onClose} disabled={saving}>ปิด</Button>
      </Modal.Footer>
    </Modal>
  )
}

// ─── DebtCard ─────────────────────────────────────────────────
function DebtCard({
  reg, loan, installments, fieldReceipts = [], onReviewField,
  onManage, onAdjust, onReset, onSettle, onBadDebt,
}: {
  reg: CustomerReg
  loan: LoanData | null
  installments: InstallmentItem[]
  fieldReceipts?: FieldReceipt[]
  onReviewField?: () => void
  onManage: () => void
  onAdjust: () => void
  onReset: () => void
  onSettle: () => void
  onBadDebt: () => void
}) {
  const remain = loan ? calcRemain(loan, installments) : 0
  const canSettle = loan ? remain <= loan.totalWithInterest * 0.5 : false
  const paid = paidCount(installments)
  const total = loan?.installmentDays || installments.length   // จำนวนงวดเดิม (ไม่เปลี่ยนแม้เพิ่มงวดเกิน)
  const maxSeq = installments.reduce((m, i) => Math.max(m, i.seq), 0)   // งวดสูงสุด (อาจเกิน total)
  const todaySeq = paid + 1
  const todayDue = installments.find((i) => i.seq === todaySeq)
  const todayPaid = todayDue?.status === 'paid'

  return (
    <Card className='shadow-sm' style={{ fontSize: 13 }}>
      <Card.Body>
        <div className='d-flex justify-content-between align-items-start mb-2'>
          <div>
            <div style={{ fontWeight: 700 }}>{reg.personal.fullName}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{formatDate(reg.meta.updatedAt)}</div>
          </div>
          <Badge bg='success' style={{ fontSize: 10 }}>กำลังผ่อน</Badge>
        </div>

        {loan ? (
          <div>
            <div>ยอดรวม: <strong>{money(loan.totalWithInterest)}</strong></div>
            <div>ยอดกู้จริง: <strong>{money(loan.actualAmount)}</strong></div>
            <div>ยอดส่ง/วัน: <strong>{money(loan.dailyPayment)}</strong></div>
            <div>ชำระแล้ว: <strong>{money(loan.totalWithInterest - remain)}</strong></div>
            <div style={{ color: '#c62828', fontWeight: 700 }}>คงเหลือ: {money(remain)}</div>
            <div>งวดจ่ายแล้ว: <strong>{paid}/{total}</strong></div>
            {maxSeq > total && (
              <div style={{ color: '#ef6c00', fontWeight: 700 }}>งวดเพิ่ม (เกิน): {maxSeq}/{total}</div>
            )}
            <div>ค้างชำระ: <strong style={{ color: '#f44336' }}>{Math.max(0, total - paid)}</strong></div>
            <div>
              สถานะวันนี้:{' '}
              <Badge bg={todayPaid ? 'success' : 'danger'} style={{ fontSize: 10 }}>
                {todayPaid ? 'จ่ายแล้ว' : 'ยังไม่ได้จ่าย'}
              </Badge>
            </div>
            <div>โทร: {reg.personal.phone || '-'}</div>
            <div>โซน: {loan.zone || '-'}</div>
          </div>
        ) : (
          <div className='text-muted'>ไม่พบข้อมูลสินเชื่อ</div>
        )}

        {fieldReceipts.length > 0 && (
          <div className='d-flex align-items-center gap-2 mt-3 p-2' style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6 }}>
            <i className='fa-solid fa-camera' style={{ color: '#f57f17' }} />
            <div style={{ flex: 1, fontSize: 12 }}>
              รับเงินภาคสนาม <strong>{fieldReceipts.length}</strong> ราย · <strong style={{ color: '#2e7d32' }}>{money(fieldReceipts.reduce((s, r) => s + (r.amount || 0), 0))}</strong>
            </div>
            <Button size='sm' variant='warning' style={{ fontSize: 11 }} onClick={onReviewField}>ตรวจ/ยืนยัน</Button>
          </div>
        )}

        <div className='mt-3 d-flex flex-column gap-2'>
          <Button variant='primary' size='sm' onClick={onManage}>
            <i className='fa-solid fa-list-check me-1' /> ลูกค้าจ่ายเงิน
          </Button>
          <div className='d-flex gap-2'>
            <Button variant='warning' size='sm' className='flex-fill' onClick={onAdjust}>ปรับดอก</Button>
            <Button variant='success' size='sm' className='flex-fill' onClick={onReset}>ล้ม เพิ่ม/ลดยอด</Button>
          </div>
          <div className='d-flex gap-2'>
            <Button variant='danger' size='sm' className='flex-fill' onClick={onBadDebt}>หนี้เสีย</Button>
            <Button
              variant={canSettle ? 'info' : 'secondary'}
              size='sm' className='flex-fill'
              onClick={onSettle}
              title={canSettle ? 'ล้มจบหนี้สิน (ปิดบัญชี)' : 'ยอดคงเหลือต้อง ≤ 50% ของยอดรวมจึงจะล้มจบได้'}
            >
              <i className='fa-solid fa-flag-checkered me-1' />ล้มจบหนี้สิน
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── Main ─────────────────────────────────────────────────────
type ModalMode = 'manage' | 'adjust' | 'reset' | 'settle' | 'badDebt' | null

export default function DebtManager() {
  const { currentUser } = React.useContext(AuthContext)
  const uid = currentUser?.uid || ''

  const [loading, setLoading] = React.useState(true)
  const [customers, setCustomers] = React.useState<CustomerReg[]>([])
  const [loans, setLoans] = React.useState<Record<string, LoanData>>({})
  const [installMap, setInstallMap] = React.useState<Record<string, InstallmentItem[]>>({})
  const [viewMode, setViewMode] = React.useState<'Grid' | 'List'>('Grid')
  const [search, setSearch] = React.useState('')
  const [zoneFilter, setZoneFilter] = React.useState('ทุกโซน')
  const [sortBy, setSortBy] = React.useState<'updatedAt' | 'createdAt' | 'name' | 'remain' | 'zone'>('updatedAt')
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc')

  // field collection (รับเงินภาคสนาม)
  const [fieldCollect, setFieldCollect] = React.useState<Record<string, FieldReceipt[]>>({})
  const [fieldOnly,    setFieldOnly]    = React.useState(false)
  const [reviewReg,    setReviewReg]    = React.useState<CustomerReg | null>(null)
  const [showReview,   setShowReview]   = React.useState(false)
  const [reviewSaving, setReviewSaving] = React.useState(false)

  // modal state
  const [modalMode, setModalMode] = React.useState<ModalMode>(null)
  const [target, setTarget] = React.useState<CustomerReg | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [confirmShow, setConfirmShow] = React.useState(false)

  // manage modal
  const [paySeq, setPaySeq] = React.useState<number>(0)
  const [payAmount, setPayAmount] = React.useState('')
  const [pickMode, setPickMode] = React.useState<'date' | 'seq'>('seq')
  const [slipFile, setSlipFile] = React.useState<File | null>(null)
  const [slipPreview, setSlipPreview] = React.useState<string | null>(null)
  const [viewSlip, setViewSlip] = React.useState<{ image: string; name: string; seq: number } | null>(null)
  const [loadingSlip, setLoadingSlip] = React.useState(false)

  // adjust modal
  const [adjRate, setAdjRate] = React.useState('0')
  const [adjDays, setAdjDays] = React.useState('30')

  // reset modal
  const [resetTopup, setResetTopup] = React.useState('')
  const [resetRate, setResetRate] = React.useState('20')
  const [resetDays, setResetDays] = React.useState('30')

  // settle modal (ล้มจบหนี้สิน)
  const [settleMode, setSettleMode] = React.useState<'close' | 'renew'>('close')
  const [settleTopup, setSettleTopup] = React.useState('')
  const [settleRate, setSettleRate] = React.useState('0')
  const [settleDays, setSettleDays] = React.useState('30')

  // bad debt
  const [badReason, setBadReason] = React.useState('')

  // เอกสาร
  const [docModal, setDocModal] = React.useState(false)
  const [docDays, setDocDays] = React.useState(17)
  const [summaryDate, setSummaryDate] = React.useState(() => new Date().toISOString().slice(0, 10))
  const [summaryGenerating, setSummaryGenerating] = React.useState(false)
  const [docGenerating, setDocGenerating] = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    try {
      const list = await getAllCustomers('active')
      setCustomers(list.sort((a, b) => b.meta.updatedAt - a.meta.updatedAt))

      // โหลดรายการรับเงินภาคสนาม (collect)
      const col = await dbRead<Record<string, Record<string, FieldReceipt>>>(DB_COLLECT)
      const fcMap: Record<string, FieldReceipt[]> = {}
      for (const [cid, recs] of Object.entries(col || {})) {
        fcMap[cid] = Object.values(recs || {}).filter(Boolean)
      }
      setFieldCollect(fcMap)

      const lMap: Record<string, LoanData> = {}
      const iMap: Record<string, InstallmentItem[]> = {}
      await Promise.all(
        list.map(async (c) => {
          const [l, ins] = await Promise.all([
            dbRead<LoanData>(PATH.loan(c.key)),
            dbRead<Record<string, InstallmentItem>>(PATH.payin(c.key)),
          ])
          if (l) lMap[c.key] = l
          if (ins) iMap[c.key] = Object.values(ins).filter(Boolean).sort((a, b) => a.seq - b.seq)
          else iMap[c.key] = []
        })
      )
      setLoans(lMap)
      setInstallMap(iMap)
    } catch (e: any) {
      toastDanger(e?.message || 'โหลดไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { loadData() }, [loadData])

  const openModal = (c: CustomerReg, mode: ModalMode) => {
    setTarget(c)
    setModalMode(mode)
    setSaving(false)
    setConfirmShow(false)
    if (mode === 'manage') {
      const ins = installMap[c.key] || []
      const next = ins.find((i) => i.status === 'pending')
      setPaySeq(next?.seq || 0)
      setPayAmount(String(loans[c.key]?.dailyPayment || ''))
    }
    if (mode === 'adjust') { setAdjRate('20'); setAdjDays('30') }
    if (mode === 'reset') { setResetTopup(String(loans[target?.key || c.key]?.actualAmount || '')); setResetRate('20'); setResetDays('30') }
    if (mode === 'settle') { setSettleMode('close'); setSettleTopup(String(loans[c.key]?.actualAmount || '')); setSettleRate('20'); setSettleDays('30') }
    if (mode === 'badDebt') setBadReason('')
  }

  const closeModal = () => { if (saving) return; setModalMode(null); setTarget(null); setSlipFile(null); setSlipPreview(null) }

  // ─── 2.4 จัดการงวด ───────────────────────────────────────────
  // ─── View slip ───────────────────────────────────────────────
  const handleViewSlip = async (slipId: string, seq: number) => {
    if (!target) return
    setLoadingSlip(true)
    try {
      const slip = await dbRead<any>(DB_SLIP_BY_ITEM_ID(target.key, slipId))
      if (!slip?.image) { toastDanger('ไม่พบรูปสลิป'); return }
      const mime = slip.type || 'image/jpeg'
      const src = slip.image.startsWith('data:') ? slip.image : `data:${mime};base64,${slip.image}`
      setViewSlip({ image: src, name: slip.name || 'slip.jpg', seq })
    } catch (e: any) {
      toastDanger(e?.message || 'โหลดสลิปไม่สำเร็จ')
    } finally {
      setLoadingSlip(false)
    }
  }

  // ─── Resize slip image ──────────────────────────────────────
  const resizeSlip = (file: File, maxSize = 1200, quality = 0.7): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        let { width, height } = img
        if (width > maxSize || height > maxSize) {
          if (width >= height) { height = Math.round(height * maxSize / width); width = maxSize }
          else { width = Math.round(width * maxSize / height); height = maxSize }
        }
        const canvas = document.createElement('canvas')
        canvas.width = width; canvas.height = height
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error('resize failed')); return }
          const reader = new FileReader()
          reader.onload = () => resolve((reader.result as string).split(',')[1])
          reader.onerror = reject
          reader.readAsDataURL(blob)
        }, 'image/jpeg', quality)
      }
      img.onerror = reject
      img.src = url
    })

  const handlePayInstallment = async () => {
    if (!target) return
    const ins = installMap[target.key] || []
    const loan = loans[target.key]
    const existing = ins.find((i) => i.seq === paySeq)
    // งวดเกิน (เพิ่มใหม่): dueAmount = ยอดค้างปัจจุบัน
    const remain = loan ? calcRemain(loan, ins) : 0
    const item = existing || { seq: paySeq, dueAmount: remain > 0 ? remain : (loan?.dailyPayment || 0), paidAmount: 0, status: 'pending' as InstallmentStatus }
    if (!paySeq || (!existing && paySeq !== nextExtraSeq(ins))) { toastDanger('ไม่พบงวดที่เลือก'); return }

    setSaving(true)
    try {
      const paidAmt = parseFloat(payAmount) || 0
      const paidAt = Date.now()
      const status = paidAmt >= item.dueAmount ? 'paid' : 'partial'

      // upload slip ถ้ามี
      let slipId: string | undefined
      if (slipFile) {
        const base64 = await resizeSlip(slipFile)
        slipId = `slip_${paidAt}_${Math.random().toString(36).slice(2, 7)}`
        await dbSet(DB_SLIP_BY_ITEM_ID(target.key, slipId), {
          id: slipId,
          image: base64,
          name: slipFile.name,
          size: slipFile.size,
          type: 'image/jpeg',
          seq: paySeq,
          paidAt,
          uploadedBy: uid,
        })
      }

      await dbUpdate(`${PATH.payin(target.key)}/${paySeq}`, {
        paidAmount: paidAmt,
        paidAt,
        paidBy: uid,
        status,
        ...(existing ? {} : { seq: paySeq, dueAmount: item.dueAmount }),   // งวดเกิน: เขียน seq+dueAmount ให้ครบ
        ...(slipId ? { slipId } : {}),
      })

      await saveLog(uid, 'payment', target.key, `งวด ${paySeq} จ่าย ${money(paidAmt)}${slipId ? ' (มีสลิป)' : ''}`)
      toastSuccess(`บันทึกการชำระงวด ${paySeq} เรียบร้อย${slipId ? ' พร้อมสลิป' : ''}`)
      setSlipFile(null)
      await loadData()
      closeModal()
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
      setConfirmShow(false)
    }
  }

  // ─── 2.1 ปรับดอก ─────────────────────────────────────────────
  const handleAdjustInterest = async () => {
    if (!target) return
    const loan = loans[target.key]
    if (!loan) { toastDanger('ไม่พบข้อมูลสินเชื่อ'); return }

    const ins = installMap[target.key] || []
    const remain = calcRemain(loan, ins)
    const newRate = parseFloat(adjRate) || 20
    const newDays = parseInt(adjDays) || 30
    const newInterest = round((remain * newRate) / 100)   // ดอกใหม่ = ยอดคงเหลือ × ดอก% ÷ 100
    const newTotal = round(remain + newInterest)        // ยอดรวมใหม่ = คงเหลือ + ดอกใหม่
    const newDaily = round(newTotal / newDays)          // ผ่อนต่อวันใหม่ = ยอดรวมใหม่ ÷ จำนวนวัน



    setSaving(true)
    try {
      // snapshot สินเชื่อเก่า
      const snapKey = `${target.key}_adj_${Date.now()}`
      await dbSet(`ktfc/loanHistory/${snapKey}`, { ...loan, snapAt: Date.now(), snapReason: 'adjust_interest' })

      // สร้างงวดใหม่
      const newInstallments: InstallmentItem[] = Array.from({ length: newDays }, (_, i) => ({
        seq: i + 1,
        dueAmount: newDaily,
        paidAmount: 0,
        status: 'pending' as const,
      }))
      const newInsMap: Record<string, InstallmentItem> = {}
      newInstallments.forEach((item) => { newInsMap[String(item.seq)] = item })

      // update loan
      const newLoan: LoanData = {
        ...loan,
        interestRate: newRate,
        interestAmount: newInterest,
        totalWithInterest: newTotal,
        dailyPayment: newDaily,
        installmentDays: newDays,
        updatedAt: Date.now(),
        updatedBy: uid,
      }
      await dbSet(PATH.loan(target.key), newLoan)
      await dbSet(PATH.payin(target.key), newInsMap)
      await saveLog(uid, 'adjust_interest', target.key, `ดอก ${newRate}% งวด ${newDays} วัน ยอดใหม่ ${money(newTotal)}`)
      toastSuccess('ปรับดอกเรียบร้อย')
      await loadData()
      closeModal()
    } catch (e: any) {
      toastDanger(e?.message || 'ปรับดอกไม่สำเร็จ')
    } finally {
      setSaving(false)
      setConfirmShow(false)
    }
  }

  // ─── 2.2 ล้มยอด ──────────────────────────────────────────────
  const handleResetLoan = async () => {
    if (!target) return
    const loan = loans[target.key]
    if (!loan) { toastDanger('ไม่พบข้อมูลสินเชื่อ'); return }

    const ins = installMap[target.key] || []
    const remain = calcRemain(loan, ins)
    const paid = ins.reduce((s, i) => s + i.paidAmount, 0)
    const topup = parseFloat(resetTopup) || 0
    const newRate = parseFloat(resetRate) || 20
    const newDays = parseInt(resetDays) || 30
    const newInterest = round((topup * newRate) / 100)
    const newTotal = round(topup + newInterest)
    const newDaily = round(newTotal / newDays)
    // เงินคืนลูกค้า (ล้มกู้เพิ่ม/ลดยอด) = ยอดกู้ใหม่ − ยอดค้างเดิม − งวดแรกใหม่
    // ตัวอย่าง: 5,000,000 − 3,000,000 − 200,000 = 1,800,000
    const refundToCustomer = round(Math.max(0, topup - remain - newDaily))

    setSaving(true)
    try {
      // snapshot
      const snapKey = `${target.key}_reset_${Date.now()}`
      await dbSet(`ktfc/loanHistory/${snapKey}`, { ...loan, snapAt: Date.now(), snapReason: 'reset_loan', remain, paid })

      const newInstallments: InstallmentItem[] = Array.from({ length: newDays }, (_, i) => {
        const item: InstallmentItem = {
          seq: i + 1,
          dueAmount: newDaily,
          paidAmount: i === 0 ? newDaily : 0,
          status: i === 0 ? ('paid' as InstallmentStatus) : ('pending' as InstallmentStatus),
        }
        if (i === 0) item.paidAt = Date.now()
        return item
      })
      const newInsMap: Record<string, InstallmentItem> = {}
      newInstallments.forEach((item) => { newInsMap[String(item.seq)] = item })

      const newLoan: LoanData = {
        ...loan,
        actualAmount: topup,
        interestRate: newRate,
        interestAmount: newInterest,
        totalWithInterest: newTotal,
        dailyPayment: newDaily,
        installmentDays: newDays,
        payoutAmount: refundToCustomer,
        updatedAt: Date.now(),
        updatedBy: uid,
      }
      await dbSet(PATH.loan(target.key), newLoan)
      await dbSet(PATH.payin(target.key), newInsMap)
      await saveLog(uid, 'reset_loan', target.key, `ล้มยอด topup ${money(topup)} คืนลูกค้า ${money(refundToCustomer)}`)
      toastSuccess(`ล้มยอดเรียบร้อย${refundToCustomer > 0 ? ` คืนลูกค้า ${money(refundToCustomer)} กีบ` : ''}`)
      await loadData()
      closeModal()
    } catch (e: any) {
      toastDanger(e?.message || 'ล้มยอดไม่สำเร็จ')
    } finally {
      setSaving(false)
      setConfirmShow(false)
    }
  }

  // ─── 2.5 ล้มจบหนี้สิน (ปิดบัญชี หรือ เริ่มกู้ใหม่) ─────────────
  const handleSettle = async () => {
    if (!target) return
    const loan = loans[target.key]
    if (!loan) { toastDanger('ไม่พบข้อมูลสินเชื่อ'); return }

    const ins = installMap[target.key] || []
    const remain = calcRemain(loan, ins)
    const threshold = loan.totalWithInterest * 0.5   // เกณฑ์ล้มจบ = ยอดรวมทั้งสัญญา × 50%

    // เงื่อนไข: ยอดคงเหลือต้อง ≤ 50% ของยอดรวม จึงจะล้มจบหนี้สินได้
    if (remain > threshold) {
      toastDanger(`ล้มจบหนี้สินไม่ได้ — ยอดคงเหลือ ${money(remain)} เกิน 50% ของยอดรวม (เกณฑ์ ≤ ${money(round(threshold))})`)
      return
    }

    // ─── โหมด เริ่มกู้ใหม่ ──────────────────────────────────────
    if (settleMode === 'renew') {
      const topup = parseFloat(settleTopup) || 0
      const newRate = parseFloat(settleRate) || 20
      const newDays = parseInt(settleDays) || 30
      if (!topup || !newDays) { toastDanger('กรอกยอดกู้ใหม่และจำนวนวันให้ถูกต้อง'); return }

      setSaving(true)
      try {
        const now = Date.now()
        const calc = calcLoan(topup, newRate, newDays)

        // ── เงินโอนให้ลูกค้า (เริ่มกู้ใหม่) ──
        // = ยอดกู้ใหม่ − ยอดค้างเดิม − งวดแรกใหม่   (หักหนี้สัญญาเดิมออกจากเงินโอน)
        // ตัวอย่าง: กู้ใหม่ 5,000,000 − ค้างเดิม 3,000,000 − งวดแรก 200,000 = 1,800,000
        // ถ้าต้องการให้ "ไม่หักหนี้ค้างเดิม" (โอนเต็มยอดหักแค่งวดแรก) เปลี่ยนเป็น  calc.payoutAmount
        const renewPayout = round(Math.max(0, topup - remain - calc.dailyPayment))

        // 1) ย้ายสัญญาเดิม → data_d (เก็บหลักฐาน — loan/payin/payout เดิมยังอยู่ใต้ key เดิม)
        const oldClosed: CustomerReg = { ...target, meta: { ...target.meta, status: 'closed', updatedAt: now } }
        await dbSet(PATH.closedCustomer(target.key), oldClosed)
        await dbUpdate(PATH.status(target.key), {
          now: { value: 'closed', lastUpdatedBy: uid, updatedAt: now },
          [`history/${now}`]: { uid, value: 'closed', action: 'settle_renew_old', updatedAt: now },
        })
        await dbRemove(PATH.activeCustomer(target.key))

        // 2) สร้างสัญญาใหม่ใน data_c ด้วย key ใหม่ (ยอดใหม่หมด, หักงวดแรกทันที)
        const newKey = genCustomerId(uid)
        const newReg: CustomerReg = {
          ...target,
          key: newKey,
          meta: { ...target.meta, status: 'paid', createdAt: now, updatedAt: now, uid, role: 'settle_renew', pageCount: 0, photoCount: 0 },
          loan: { ...target.loan, wantedAmount: topup, installmentDays: newDays },
        }
        const newLoan: LoanData = {
          actualAmount: calc.actualAmount,
          wantedAmount: topup,
          interestRate: newRate,
          interestAmount: calc.interestAmount,
          totalWithInterest: calc.totalWithInterest,
          dailyPayment: calc.dailyPayment,
          installmentDays: newDays,
          payoutAmount: renewPayout,
          zone: loan.zone,
          purpose: loan.purpose || '',
          updatedAt: now,
          updatedBy: uid,
        }
        const items: InstallmentItem[] = Array.from({ length: newDays }, (_, i) => {
          const item: InstallmentItem = {
            seq: i + 1,
            dueAmount: calc.dailyPayment,
            paidAmount: i === 0 ? calc.dailyPayment : 0,
            status: i === 0 ? ('paid' as InstallmentStatus) : ('pending' as InstallmentStatus),
          }
          if (i === 0) item.paidAt = now
          return item
        })
        const payinMap: Record<string, InstallmentItem> = {}
        items.forEach((it) => { payinMap[String(it.seq)] = it })

        await dbSet(PATH.activeCustomer(newKey), newReg)
        await dbSet(PATH.loan(newKey), newLoan)
        await dbSet(PATH.payin(newKey), payinMap)
        await dbSet(PATH.payout(newKey), { amount: newLoan.payoutAmount, createdAt: now, uid, updatedAt: now })
        await dbUpdate(PATH.status(newKey), {
          now: { value: 'paid', lastUpdatedBy: uid, updatedAt: now },
          [`history/${now}`]: { uid, value: 'paid', action: 'settle_renew', updatedAt: now },
        })

        await saveLog(uid, 'close_account', target.key, `ล้มจบหนี้สิน (กู้ใหม่) — ปิดสัญญาเดิม คงเหลือ ${money(remain)}`)
        await saveLog(uid, 'create', newKey, `เริ่มกู้ใหม่จากสัญญาเดิม ${target.key} — ยอด ${money(topup)} งวด ${newDays} วัน`)

        toastSuccess(`ล้มจบหนี้สิน + เริ่มกู้ใหม่ ${target.personal.fullName} เรียบร้อย`)
        await loadData()
        closeModal()
      } catch (e: any) {
        toastDanger(e?.message || 'ดำเนินการไม่สำเร็จ')
      } finally {
        setSaving(false)
        setConfirmShow(false)
      }
      return
    }

    // ─── โหมด ปิดบัญชี ─────────────────────────────────────────
    setSaving(true)
    try {
      const now = Date.now()
      const updated: CustomerReg = { ...target, meta: { ...target.meta, status: 'closed', updatedAt: now } }
      await dbSet(PATH.closedCustomer(target.key), updated)
      await dbUpdate(PATH.status(target.key), {
        now: { value: 'closed', lastUpdatedBy: uid, updatedAt: now },
        [`history/${now}`]: { uid, value: 'closed', action: 'settle_close', updatedAt: now },
      })
      await dbRemove(PATH.activeCustomer(target.key))
      await saveLog(uid, 'close_account', target.key, `ล้มจบหนี้สิน (ปิดบัญชี) — คงเหลือ ${money(remain)} / ยอดรวม ${money(loan.totalWithInterest)}`)

      toastSuccess(`ปิดบัญชี ${target.personal.fullName} → data_d เรียบร้อย`)
      await loadData()
      closeModal()
    } catch (e: any) {
      toastDanger(e?.message || 'ปิดบัญชีไม่สำเร็จ')
    } finally {
      setSaving(false)
      setConfirmShow(false)
    }
  }

  // ─── 2.3 หนี้เสีย ─────────────────────────────────────────────
  const handleBadDebt = async () => {
    if (!target) return
    setSaving(true)
    try {
      await markBadDebt(uid, target.key, target, badReason || 'marked_bad_debt')
      toastSuccess(`ย้าย ${target.personal.fullName} ไปหนี้เสียแล้ว`)
      await loadData()
      closeModal()
    } catch (e: any) {
      toastDanger(e?.message || 'ไม่สำเร็จ')
    } finally {
      setSaving(false)
      setConfirmShow(false)
    }
  }

  // ─── Filter ───────────────────────────────────────────────────
  const allZones = React.useMemo(() => {
    const set = new Set<string>()
    customers.forEach((c) => {
      const z = loans[c.key]?.zone
      if (z) set.add(z)
    })
    return ['ทุกโซน', ...Array.from(set).sort()]
  }, [customers, loans])

  // รายการรับเงินภาคสนามที่ยัง pending และอยู่ภายใน 2 วัน
  const recentPending = React.useCallback((id: string): FieldReceipt[] => {
    const cutoff = Date.now() - FIELD_WINDOW_MS
    return (fieldCollect[id] || []).filter((r) => (r.status || 'pending') === 'pending' && r.collectedAt >= cutoff)
  }, [fieldCollect])

  const pendingCustomerCount = React.useMemo(
    () => Object.keys(fieldCollect).filter((id) => recentPending(id).length > 0).length,
    [fieldCollect, recentPending]
  )

  // ยืนยันรับเงินภาคสนาม → ลงงวดที่เลือกเข้า payin + mark receipt confirmed
  const confirmReceipt = async (reg: CustomerReg, receipt: FieldReceipt, seq: number, amount: number) => {
    const ins = installMap[reg.key] || []
    const loan = loans[reg.key]
    const existing = ins.find((i) => i.seq === seq)
    const remain = loan ? calcRemain(loan, ins) : 0
    const item = existing || { seq, dueAmount: remain > 0 ? remain : (loan?.dailyPayment || amount), paidAmount: 0, status: 'pending' as InstallmentStatus }
    if (!existing && seq !== nextExtraSeq(ins)) { toastDanger('ไม่พบงวดที่เลือก'); return }
    if (amount <= 0) { toastDanger('กรุณาระบุจำนวนเงินให้ถูกต้อง'); return }
    setReviewSaving(true)
    try {
      const paidAt = Date.now()
      const status = amount >= item.dueAmount ? 'paid' : 'partial'

      // ใช้รูปภาคสนามเป็นสลิปของงวดนั้น (ถ้ามี)
      let slipId: string | undefined
      if (receipt.hasImage) {
        const imgDoc = await dbRead<{ image?: string }>(DB_COLLECT_IMG_ITEM(reg.key, receipt.id))
        if (imgDoc?.image) {
          slipId = `slip_${paidAt}_${Math.random().toString(36).slice(2, 7)}`
          await dbSet(DB_SLIP_BY_ITEM_ID(reg.key, slipId), {
            id: slipId, image: imgDoc.image, name: 'field_receipt', size: 0,
            type: 'image/jpeg', seq, paidAt, uploadedBy: uid,
          })
        }
      }

      await dbUpdate(`${PATH.payin(reg.key)}/${seq}`, {
        paidAmount: amount, paidAt, paidBy: uid, status,
        ...(existing ? {} : { seq, dueAmount: item.dueAmount }),   // งวดเกิน: เขียน seq+dueAmount ให้ครบ
        ...(slipId ? { slipId } : {}),
      })
      await dbUpdate(`${DB_COLLECT_BY_ID(reg.key)}/${receipt.id}`, {
        status: 'confirmed', confirmedBy: uid, confirmedAt: paidAt, appliedSeq: seq, appliedAmount: amount,
      })
      await saveLog(uid, 'payment', reg.key, `รับเงินภาคสนาม → งวด ${seq} จ่าย ${money(amount)}`)

      // อัปเดต state ในตัว (ไม่ reload ทั้งหน้า) — งวดเกินให้ append
      setInstallMap((prev) => {
        const arr = prev[reg.key] || []
        const has = arr.some((i) => i.seq === seq)
        const updated = has
          ? arr.map((i) => i.seq === seq ? { ...i, paidAmount: amount, paidAt, status: status as InstallmentStatus, ...(slipId ? { slipId } : {}) } : i)
          : [...arr, { seq, dueAmount: item.dueAmount, paidAmount: amount, paidAt, status: status as InstallmentStatus, ...(slipId ? { slipId } : {}) }].sort((a, b) => a.seq - b.seq)
        return { ...prev, [reg.key]: updated }
      })
      setFieldCollect((prev) => ({
        ...prev,
        [reg.key]: (prev[reg.key] || []).map((r) => r.id === receipt.id ? { ...r, status: 'confirmed' } : r),
      }))
      toastSuccess(`ลงงวด ${seq} เข้าบัญชีเรียบร้อย (${money(amount)})`)
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setReviewSaving(false)
    }
  }

  const filtered = React.useMemo(() => {
    const arr = customers.filter((c) => {
      const l = loans[c.key]
      const matchSearch = !search ||
        c.personal.fullName.toLowerCase().includes(search.toLowerCase()) ||
        c.personal.phone?.includes(search) ||
        c.key.includes(search) ||
        (l?.zone || '').includes(search)
      const matchZone = zoneFilter === 'ทุกโซน' || (l?.zone || '') === zoneFilter
      if (fieldOnly && recentPending(c.key).length === 0) return false
      return matchSearch && matchZone
    })

    const dir = sortDir === 'asc' ? 1 : -1
    const remainOf = (c: CustomerReg) => {
      const l = loans[c.key]
      return l ? calcRemain(l, installMap[c.key] || []) : 0
    }
    arr.sort((a, b) => {
      let cmp = 0
      switch (sortBy) {
        case 'name': cmp = a.personal.fullName.localeCompare(b.personal.fullName, 'th'); break
        case 'createdAt': cmp = (a.meta.createdAt || 0) - (b.meta.createdAt || 0); break
        case 'updatedAt': cmp = (a.meta.updatedAt || 0) - (b.meta.updatedAt || 0); break
        case 'remain': cmp = remainOf(a) - remainOf(b); break
        case 'zone': cmp = String(loans[a.key]?.zone || '').localeCompare(String(loans[b.key]?.zone || ''), 'th'); break
        default: cmp = 0
      }
      return cmp * dir
    })
    return arr
  }, [customers, loans, installMap, search, zoneFilter, sortBy, sortDir, fieldOnly, recentPending])

  // ─── Adjust/Reset preview ─────────────────────────────────────
  const adjPreview = React.useMemo(() => {
    if (!target || modalMode !== 'adjust') return null
    const loan = loans[target.key]
    if (!loan) return null
    const ins = installMap[target.key] || []
    const remain = calcRemain(loan, ins)
    const newRate = parseFloat(adjRate) || 20
    const newDays = parseInt(adjDays) || 30
    const newInterest = round((remain * newRate) / 100)
    const newTotal = round(remain + newInterest)
    const newDaily = round(newTotal / newDays)
    return { remain, newRate, newDays, newInterest, newTotal, newDaily }
  }, [target, modalMode, adjRate, adjDays, loans, installMap])

  const resetPreview = React.useMemo(() => {
    if (!target || modalMode !== 'reset') return null
    const loan = loans[target.key]
    if (!loan) return null
    const ins = installMap[target.key] || []
    const remain = calcRemain(loan, ins)
    const paid = ins.reduce((s, i) => s + i.paidAmount, 0)
    const topup = parseFloat(resetTopup) || 0
    const newRate = parseFloat(resetRate) || 20
    const newDays = parseInt(resetDays) || 30
    const newInterest = round((topup * newRate) / 100)
    const newTotal = round(topup + newInterest)
    const newDaily = round(newTotal / newDays)
    const refund = round(Math.max(0, topup - remain - newDaily))
    return { remain, paid, topup, newRate, newDays, newInterest, newTotal, newDaily, refund }
  }, [target, modalMode, resetTopup, resetRate, resetDays, loans, installMap])

  // ─── Settle (ล้มจบหนี้สิน) preview ────────────────────────────
  const settlePreview = React.useMemo(() => {
    if (!target || modalMode !== 'settle') return null
    const loan = loans[target.key]
    if (!loan) return null
    const ins = installMap[target.key] || []
    const remain = calcRemain(loan, ins)
    const total = loan.totalWithInterest
    const threshold = round(total * 0.5)
    const paid = Math.max(0, total - remain)
    const pctRemain = total > 0 ? (remain / total) * 100 : 0
    const eligible = remain <= threshold
    return { total, remain, threshold, paid, pctRemain, eligible }
  }, [target, modalMode, loans, installMap])

  // ─── Renew (เริ่มกู้ใหม่หลังล้มจบ) preview ─────────────────────
  const settleRenewPreview = React.useMemo(() => {
    if (!target || modalMode !== 'settle' || settleMode !== 'renew') return null
    const topup = parseFloat(settleTopup) || 0
    const rate = parseFloat(settleRate) || 0
    const days = parseInt(settleDays) || 30
    if (!topup || !days) return null
    const calc = calcLoan(topup, rate, days)
    return { topup, rate, days, ...calc }
  }, [target, modalMode, settleMode, settleTopup, settleRate, settleDays])

  // ─── สร้างเอกสาร ───────────────────────────────────────────────
  const handleGenerateDoc = async (zone: 'A' | 'B' | 'C') => {
    setDocGenerating(true)
    try {
      // กรองลูกค้าตาม zone
      const zoneCustomers = customers.filter((c) => {
        const l = loans[c.key]
        const z = l?.zone || ''
        const zones = Array.isArray(z) ? z : [z]
        return zones.some((zz: string) => zz.trim().toUpperCase() === zone)
      })

      if (zoneCustomers.length === 0) {
        toastDanger(`ไม่พบลูกค้าในโซน ${zone}`)
        return
      }

      // สร้าง date range 17 วันย้อนหลัง + วันนี้
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const dateRange: Date[] = []
      for (let i = docDays - 1; i >= 0; i--) {
        const d = new Date(today)
        d.setDate(d.getDate() - i)
        dateRange.push(d)
      }

      const rows: DebtSheetRow[] = zoneCustomers.map((c) => {
        const loan = loans[c.key]
        const ins = installMap[c.key] || []
        const remain = loan ? calcRemain(loan, ins) : 0

        // สร้าง payment map จาก installments
        const payments: Record<string, number | string> = {}
        dateRange.forEach((d) => {
          const key = `${d.getDate()}/${d.getMonth() + 1}`
          // หา installment ที่ตรงกับวันนี้
          const paidOnDay = ins.find((inst) => {
            if (!inst.paidAt) return false
            const pd = new Date(inst.paidAt)
            return pd.getDate() === d.getDate() &&
              pd.getMonth() === d.getMonth() &&
              pd.getFullYear() === d.getFullYear()
          })
          if (paidOnDay && paidOnDay.paidAmount > 0) {
            payments[key] = paidOnDay.paidAmount
          } else if (d <= today) {
            payments[key] = '*'
          } else {
            payments[key] = ''
          }
        })

        return {
          dateStart: new Date(loan?.updatedAt || c.meta.createdAt),
          days: loan?.installmentDays || 0,
          name: c.personal.fullName,
          amount: loan?.actualAmount || 0,
          dailyPayment: loan?.dailyPayment || 0,
          remaining: remain,
          payments,
          note: '',
        }
      })

      await generateDebtSheet({
        zone,
        dateRange,
        rows,
        filename: `สาย${zone}_${today.toISOString().slice(0, 10)}.xlsx`,
      })
      toastSuccess(`ดาวน์โหลดสาย${zone} เรียบร้อย`)
      setDocModal(false)
    } catch (e: any) {
      toastDanger(e?.message || 'สร้างเอกสารไม่สำเร็จ')
    } finally {
      setDocGenerating(false)
    }
  }

  // ─── สรุปยอดที่ต้องเก็บ (รายวัน) ────────────────────────────────
  const handleGenerateSummary = async () => {
    if (customers.length === 0) {
      toastDanger('ไม่มีข้อมูลลูกค้า')
      return
    }
    setSummaryGenerating(true)
    try {
      const selDate = new Date(summaryDate + 'T00:00:00')
      const MS_DAY = 24 * 60 * 60 * 1000

      const rows: CollectionRow[] = customers.map((c) => {
        const loan = loans[c.key]
        const ins = installMap[c.key] || []

        const z = loan?.zone
        const zones = Array.isArray(z) ? z : [z || '']
        const zoneStr = zones.filter(Boolean).join(', ') || '-'

        const dailyPayment = loan?.dailyPayment || 0
        const remaining = loan ? calcRemain(loan, ins) : 0
        const closed = remaining <= 0

        // วันที่เริ่มสัญญา
        const startDate = new Date(loan?.updatedAt || c.meta.createdAt)
        startDate.setHours(0, 0, 0, 0)

        // จำนวนวันที่ผ่านมานับจากวันเริ่ม ถึงวันที่เลือก (รวมวันเริ่ม = วันที่ 1)
        const daysPassed = Math.max(0, Math.floor((selDate.getTime() - startDate.getTime()) / MS_DAY) + 1)
        const paid = paidCount(ins)
        const overdueDays = closed ? 0 : Math.max(0, daysPassed - paid)

        // ยอดที่ต้องเก็บวันนี้ = ค้างสะสม + งวดวันนี้ (อย่างน้อย 1 งวด ถ้ายังไม่ปิดยอดและยังไม่เกินวันสุดท้าย)
        const dueToday = closed ? 0 : (overdueDays > 0 ? overdueDays : (daysPassed > 0 && daysPassed <= (loan?.installmentDays || 0) ? 1 : 0))
        const toCollect = Math.min(dailyPayment * dueToday, remaining)

        return {
          name: c.personal.fullName,
          zone: zoneStr,
          dailyPayment,
          overdueDays,
          toCollect,
          remaining,
          closed,
        }
      })

      await generateCollectionSummary({
        date: selDate,
        rows,
        filename: `สรุปยอดเก็บ_${summaryDate}.xlsx`,
      })
      toastSuccess('ดาวน์โหลดสรุปยอดที่ต้องเก็บเรียบร้อย')
    } catch (e: any) {
      toastDanger(e?.message || 'สร้างสรุปไม่สำเร็จ')
    } finally {
      setSummaryGenerating(false)
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
        <Spinner animation='border' variant='warning' />
      </div>
    )
  }

  // ─── Confirm dialogs ──────────────────────────────────────────
  const confirmAction = () => {
    if (modalMode === 'manage') handlePayInstallment()
    if (modalMode === 'adjust') handleAdjustInterest()
    if (modalMode === 'reset') handleResetLoan()
    if (modalMode === 'settle') handleSettle()
    if (modalMode === 'badDebt') handleBadDebt()
  }

  const confirmMsg: Record<string, string> = {
    manage: `ยืนยันบันทึกการชำระงวด ${paySeq} จำนวน ${money(parseFloat(payAmount) || 0)} กีบ?`,
    adjust: adjPreview ? `ยืนยันปรับดอก ยอดใหม่ ${money(adjPreview.newTotal)} กีบ ส่ง ${money(adjPreview.newDaily)}/วัน?` : '',
    reset: resetPreview ? `ยืนยันล้มยอด ยอดใหม่ ${money(resetPreview.newTotal)} กีบ${resetPreview.refund > 0 ? ` คืนลูกค้า ${money(resetPreview.refund)}` : ''}?` : '',
    settle: settlePreview ? (settleMode === 'renew'
      ? `ยืนยันล้มจบหนี้สิน + เริ่มกู้ใหม่ ${money(settleRenewPreview?.topup || 0)} กีบ? สัญญาเดิมจะถูกปิด (เก็บใน data_d) แล้วสร้างสัญญาใหม่ใน data_c`
      : `ยืนยันล้มจบหนี้สิน (ปิดบัญชี) ${target?.personal.fullName}? ยอดคงเหลือ ${money(settlePreview.remain)} กีบ → ย้ายไป data_d`) : '',
    badDebt: `ยืนยันย้าย ${target?.personal.fullName} ไปหนี้เสีย? ไม่สามารถย้อนกลับได้`,
  }

  return (
    <div className='p-3'>
      <ConfirmAlert
        show={confirmShow}
        title={modalMode === 'badDebt' ? '⚠️ ยืนยันหนี้เสีย' : 'ยืนยันการดำเนินการ'}
        message={confirmMsg[modalMode || ''] || 'ยืนยัน?'}
        confirmLabel={saving ? 'กำลังบันทึก...' : 'ยืนยัน'}
        variant={modalMode === 'badDebt' ? 'danger' : 'primary'}
        onConfirm={confirmAction}
        onCancel={() => setConfirmShow(false)}
      />

      {/* ─── Header ───────────────────────────────────────────── */}
      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>จัดการหนี้สิน</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>
            active {customers.length} รายการ / แสดงแล้ว {filtered.length} รายการ
          </div>
        </div>
        <div className='d-flex gap-2'>
          <Button size='sm' variant={viewMode === 'Grid' ? 'warning' : 'outline-secondary'} onClick={() => setViewMode('Grid')}>Grid</Button>
          <Button size='sm' variant={viewMode === 'List' ? 'warning' : 'outline-secondary'} onClick={() => setViewMode('List')}>List</Button>
          <Button size='sm' variant='outline-secondary' onClick={loadData}><i className='fa-solid fa-rotate-right' /></Button>
          <Button size='sm' variant='outline-warning' onClick={() => setDocModal(true)}>
            <i className='fa-solid fa-file-excel me-1' />ข้อมูลเอกสาร
          </Button>
        </div>
      </div>

      {/* ─── Filter ───────────────────────────────────────────── */}
      <Row className='g-2 mb-3'>
        <Col xs={12} md={5}>
          <Form.Control
            size='sm' placeholder='ค้นหา ชื่อ / เบอร์ / key / zone'
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col xs={8} md={3}>
          <InputGroup size='sm'>
            <InputGroup.Text title='เรียงตาม'><i className='fa-solid fa-arrow-down-wide-short' /></InputGroup.Text>
            <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
              <option value='updatedAt'>อัปเดตล่าสุด</option>
              <option value='createdAt'>วันที่ลงทะเบียน</option>
              <option value='name'>ชื่อ</option>
              <option value='remain'>ยอดคงเหลือ</option>
              <option value='zone'>โซน</option>
            </Form.Select>
          </InputGroup>
        </Col>
        <Col xs={4} md={1}>
          <Button
            size='sm' variant='outline-secondary' className='w-100'
            title={sortDir === 'asc' ? 'น้อย → มาก / เก่า → ใหม่' : 'มาก → น้อย / ใหม่ → เก่า'}
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          >
            <i className={`fa-solid ${sortDir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
          </Button>
        </Col>
        <Col xs={8} md={2}>
          <Form.Select size='sm' value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
            {allZones.map((z) => <option key={z} value={z}>{z}</option>)}
          </Form.Select>
        </Col>
        <Col xs={4} md={1}>
          <Button
            size='sm' variant='outline-danger' className='w-100' title='ล้างตัวกรอง'
            onClick={() => { setSearch(''); setZoneFilter('ทุกโซน'); setSortBy('updatedAt'); setSortDir('desc') }}
          >
            <i className='fa-solid fa-eraser' />
          </Button>
        </Col>
      </Row>

      <div className='d-flex align-items-center gap-2 mb-3 flex-wrap'>
        <Form.Check
          type='checkbox' id='fieldOnly'
          label={`แสดงเฉพาะที่รับเงินภาคสนาม (ภายใน 2 วัน)${pendingCustomerCount ? ` · ${pendingCustomerCount} ราย` : ''}`}
          checked={fieldOnly} onChange={(e) => setFieldOnly(e.target.checked)}
          style={{ fontSize: 13 }}
        />
      </div>

      {filtered.length === 0 && <Alert variant='info' style={{ fontSize: 13 }}>ไม่พบรายการ</Alert>}

      {/* ─── Grid ─────────────────────────────────────────────── */}
      {viewMode === 'Grid' && (
        <Row className='g-3'>
          {filtered.map((c) => (
            <Col key={c.key} xs={12} md={6} lg={3}>
              <DebtCard
                reg={c}
                loan={loans[c.key] || null}
                installments={installMap[c.key] || []}
                fieldReceipts={recentPending(c.key)}
                onReviewField={() => { setReviewReg(c); setShowReview(true) }}
                onManage={() => openModal(c, 'manage')}
                onAdjust={() => openModal(c, 'adjust')}
                onReset={() => openModal(c, 'reset')}
                onSettle={() => openModal(c, 'settle')}
                onBadDebt={() => openModal(c, 'badDebt')}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* ─── List ─────────────────────────────────────────────── */}
      {viewMode === 'List' && (
        <Table size='sm' bordered hover style={{ fontSize: 12 }}>
          <thead className='table-light'>
            <tr>
              <th>ชื่อ</th><th>ยอดรวม</th><th>ยอดส่ง/วัน</th><th>ชำระแล้ว</th><th>คงเหลือ</th><th>งวด</th><th>ค้าง</th><th>โซน</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const l = loans[c.key]
              const ins = installMap[c.key] || []
              const rem = l ? calcRemain(l, ins) : 0
              const pd = paidCount(ins)
              const canSettle = l ? rem <= l.totalWithInterest * 0.5 : false
              return (
                <tr key={c.key}>
                  <td>{c.personal.fullName}</td>
                  <td>{l ? money(l.totalWithInterest) : '-'}</td>
                  <td>{l ? money(l.dailyPayment) : '-'}</td>
                  <td>{l ? money(l.totalWithInterest - rem) : '-'}</td>
                  <td style={{ color: '#c62828', fontWeight: 700 }}>{l ? money(rem) : '-'}</td>
                  <td>{(() => {
                    const orig = l?.installmentDays || ins.length
                    const mx = ins.reduce((m, i) => Math.max(m, i.seq), 0)
                    return mx > orig ? <span style={{ color: '#ef6c00', fontWeight: 700 }}>{mx}/{orig}</span> : `${pd}/${orig}`
                  })()}</td>
                  <td style={{ color: '#f44336' }}>{Math.max(0, (l?.installmentDays || ins.length) - pd)}</td>
                  <td>{l?.zone || '-'}</td>
                  <td>
                    <div className='d-flex gap-1 flex-wrap'>
                      <Button size='sm' variant='primary' onClick={() => openModal(c, 'manage')}>จัดการ</Button>
                      <Button size='sm' variant='warning' onClick={() => openModal(c, 'adjust')}>ปรับดอก</Button>
                      <Button size='sm' variant={canSettle ? 'success' : 'outline-secondary'} onClick={() => openModal(c, 'settle')}>ล้มจบ</Button>
                      <Button size='sm' variant='outline-success' onClick={() => openModal(c, 'reset')}>ล้มกู้เพิ่ม</Button>
                      <Button size='sm' variant='danger' onClick={() => openModal(c, 'badDebt')}>หนี้เสีย</Button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      {/* ════════════════════════════════════════════════════════
          Modal — จัดการงวด (2.4)
      ════════════════════════════════════════════════════════ */}
      <Modal show={modalMode === 'manage'} onHide={closeModal} size='lg' centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className='fa-solid fa-list-check me-2' />จัดการหนี้สิน — {target?.personal.fullName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {target && (
            <>
              {/* Installment table */}
              <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 16 }}>
                <Table size='sm' bordered style={{ fontSize: 12 }}>
                  <thead className='table-light sticky-top'>
                    <tr>
                      <th>วันที่/งวด</th><th>ยอดที่ต้องจ่าย</th><th>จ่ายจริง</th><th>วันที่จ่าย</th><th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(installMap[target.key] || []).map((ins) => (
                      <tr
                        key={ins.seq}
                        style={{ backgroundColor: ins.seq === paySeq ? '#fffbf0' : undefined, cursor: 'pointer' }}
                        onClick={() => { setPaySeq(ins.seq); setPayAmount(String(ins.dueAmount)) }}
                      >
                        <td>{(ins as any).dueAt ? <><p className='p-0 m-0'>{formatDateShort((ins as any).dueAt)}</p><p className='p-0 m-0'>งวดที่ {ins.seq}</p></> : `งวด ${ins.seq}`}</td>
                        <td>{money(ins.dueAmount)}</td>
                        <td>{ins.paidAmount > 0 ? money(ins.paidAmount) : '-'}</td>
                        <td>{ins.paidAt ? formatDateShort(ins.paidAt) : '-'}</td>
                        <td>
                          <div className='d-flex align-items-center gap-1'>
                            <Badge bg={
                              ins.status === 'paid' ? 'success' :
                                ins.status === 'partial' ? 'warning' :
                                  ins.status === 'skipped' ? 'secondary' : 'danger'
                            } style={{ fontSize: 10 }}>
                              {ins.status === 'paid' ? 'จ่ายแล้ว' : ins.status === 'partial' ? 'จ่ายบางส่วน' : ins.status === 'skipped' ? 'ข้าม' : 'รอชำระ'}
                            </Badge>
                            {(ins as any).slipId && (
                              <Button
                                size='sm' variant='outline-primary'
                                style={{ fontSize: 10, padding: '1px 5px' }}
                                disabled={loadingSlip}
                                onClick={(e) => { e.stopPropagation(); handleViewSlip((ins as any).slipId, ins.seq) }}
                              >
                                <i className='fa-solid fa-image' />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Payment form */}
              <Card style={{ backgroundColor: '#fffbf0', borderColor: '#ffc107' }}>
                <Card.Body>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                    บันทึกการชำระ — {(() => {
                      const cur = (installMap[target.key] || []).find((i) => i.seq === paySeq)
                      const dueAt = (cur as any)?.dueAt
                      return dueAt ? `วันที่ ${formatDateShort(dueAt)}` : `งวดที่ ${paySeq}`
                    })()}
                  </div>
                  <Row className='g-2'>
                    <Col xs={12}>
                      <div className='d-flex align-items-center gap-3 flex-wrap'>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>เลือกงวดโดย:</span>
                        <Form.Check inline type='radio' id='pickmode-seq' name='pickMode'
                          label={<span style={{ fontSize: 12 }}>ลำดับงวด</span>}
                          checked={pickMode === 'seq'} onChange={() => setPickMode('seq')} />
                        <Form.Check inline type='radio' id='pickmode-date' name='pickMode'
                          label={<span style={{ fontSize: 12 }}>วันที่ (วัน/เดือน/ปี)</span>}
                          checked={pickMode === 'date'} onChange={() => setPickMode('date')} />
                      </div>
                    </Col>

                    {pickMode === 'date' ? (
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12 }}>วันที่ของงวด</Form.Label>
                          <Form.Control
                            size='sm' type='date'
                            value={(() => {
                              const cur = (installMap[target.key] || []).find((i) => i.seq === paySeq)
                              return cur ? toDateInput(dueMsOf(cur, target.key)) : ''
                            })()}
                            min={(() => {
                              const l = installMap[target.key] || []
                              return l.length ? toDateInput(dueMsOf(l[0], target.key)) : undefined
                            })()}
                            max={(() => {
                              const l = installMap[target.key] || []
                              return l.length ? toDateInput(dueMsOf(l[l.length - 1], target.key)) : undefined
                            })()}
                            onChange={(e) => {
                              const val = e.target.value
                              const found = (installMap[target.key] || []).find(
                                (i) => toDateInput(dueMsOf(i, target.key)) === val
                              )
                              if (found) { setPaySeq(found.seq); setPayAmount(String(found.dueAmount)) }
                              else if (val) toastDanger('ไม่พบงวดของวันที่นี้')
                            }}
                          />
                        </Form.Group>
                      </Col>
                    ) : (
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12 }}>เลือกงวด</Form.Label>
                          <Form.Select
                            size='sm' value={paySeq}
                            onChange={(e) => {
                              const seq = Number(e.target.value)
                              setPaySeq(seq)
                              const insList = installMap[target.key] || []
                              const f = insList.find((i) => i.seq === seq)
                              if (f) setPayAmount(String(f.dueAmount))
                              else { const l = loans[target.key]; setPayAmount(String(l ? calcRemain(l, insList) : 0)) }
                            }}
                          >
                            {(installMap[target.key] || []).map((ins) => {
                              const ms = dueMsOf(ins, target.key)
                              return (
                                <option key={ins.seq} value={ins.seq}>
                                  งวด {ins.seq}{ms ? ` · ${formatDateShort(ms)}` : ''} — {money(ins.dueAmount)} ({ins.status})
                                </option>
                              )
                            })}
                            {(() => {
                              const insList = installMap[target.key] || []
                              const l = loans[target.key]
                              const remain = l ? calcRemain(l, insList) : 0
                              if (remain <= 0) return null
                              const ns = nextExtraSeq(insList)
                              const orig = l?.installmentDays || insList.length
                              return <option value={ns}>➕ เพิ่มงวด ({ns}/{orig}) — ค้าง {money(remain)}</option>
                            })()}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    )}
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12 }}>จำนวนที่จ่าย (กีบ)</Form.Label>
                        <Form.Control
                          size='sm' type='number'
                          value={payAmount}
                          onChange={(e) => setPayAmount(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12 }}>หลักฐาน (สลิป/ภาพเงินสด)</Form.Label>
                        <Form.Control
                          size='sm' type='file' accept='image/*'
                          onChange={(e) => {
                            const file = (e.target as HTMLInputElement).files?.[0] || null
                            setSlipFile(file)
                            if (file) {
                              const url = URL.createObjectURL(file)
                              setSlipPreview(url)
                            } else {
                              setSlipPreview(null)
                            }
                          }}
                        />
                        {slipPreview && (
                          <div className='mt-2 d-flex align-items-start gap-2'>
                            <img
                              src={slipPreview}
                              alt='slip preview'
                              style={{ maxWidth: 160, maxHeight: 120, objectFit: 'contain', borderRadius: 6, border: '1px solid #e0e0e0' }}
                            />
                            <Button
                              size='sm' variant='outline-danger' style={{ fontSize: 11 }}
                              onClick={() => { setSlipFile(null); setSlipPreview(null) }}
                            >✕ ลบ</Button>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm' onClick={closeModal} disabled={saving}>ยกเลิก</Button>
          <Button variant='success' size='sm' disabled={saving || !paySeq} onClick={() => setConfirmShow(true)}>
            {saving ? 'กำลังบันทึก...' : 'ยืนยันชำระ'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          Modal — ปรับดอก (2.1)
      ════════════════════════════════════════════════════════ */}
      <Modal show={modalMode === 'adjust'} onHide={closeModal} fullscreen centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className='fa-solid fa-percent me-2 text-warning' />ปรับดอก — {target?.personal.fullName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {target && adjPreview && (
            <>
              <Row className='g-2 mb-3'>
                <Col xs={12} md={6} style={{ overflowY: "scroll", height: "calc(100vh - 160px)" }}>
                  <Table size='sm' bordered style={{ fontSize: 12 }}>
                    <thead className='table-light'>
                      <tr>
                        <th>วันที่</th><th>ยอดที่ต้องจ่าย</th><th>จ่ายจริง</th><th>วันที่จ่าย</th><th>สถานะ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(installMap[target.key] || []).map((ins) => (
                        <tr
                          key={ins.seq}
                          style={{ backgroundColor: ins.seq === paySeq ? '#fffbf0' : undefined, cursor: 'pointer' }}
                          onClick={() => { setPaySeq(ins.seq); setPayAmount(String(ins.dueAmount)) }}
                        >
                          <td>{(ins as any).dueAt ? formatDateShort((ins as any).dueAt) : `งวด ${ins.seq}`}</td>
                          <td>{money(ins.dueAmount)}</td>
                          <td>{ins.paidAmount > 0 ? money(ins.paidAmount) : '-'}</td>
                          <td>{ins.paidAt ? formatDateShort(ins.paidAt) : '-'}</td>
                          <td>
                            <div className='d-flex align-items-center gap-1'>
                              <Badge bg={
                                ins.status === 'paid' ? 'success' :
                                  ins.status === 'partial' ? 'warning' :
                                    ins.status === 'skipped' ? 'secondary' : 'danger'
                              } style={{ fontSize: 10 }}>
                                {ins.status === 'paid' ? 'จ่ายแล้ว' : ins.status === 'partial' ? 'จ่ายบางส่วน' : ins.status === 'skipped' ? 'ข้าม' : 'รอชำระ'}
                              </Badge>
                              {(ins as any).slipId && (
                                <Button
                                  size='sm' variant='outline-primary'
                                  style={{ fontSize: 10, padding: '1px 5px' }}
                                  disabled={loadingSlip}
                                  onClick={(e) => { e.stopPropagation(); handleViewSlip((ins as any).slipId, ins.seq) }}
                                >
                                  <i className='fa-solid fa-image' />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
                <Col xs={12} md={6}>
                  <Row className='g-2 mb-3'>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>ดอกเบี้ยใหม่ (%)</Form.Label>
                        <Form.Control size='sm' type='number' value={adjRate} onChange={(e) => setAdjRate(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>จำนวนวันใหม่</Form.Label>
                        <Form.Control size='sm' type='number' value={adjDays} onChange={(e) => setAdjDays(e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className='g-2'>
                    <Col xs={12} md={6}>
                      <SummaryTable title='สัญญาปัจจุบัน' rows={[
                        { label: 'ยอดคงเหลือ (ฐานใหม่)', value: money(adjPreview.remain), highlight: true },
                        { label: 'ดอกเดิม', value: `${loans[target.key]?.interestRate || 0}%` },
                        { label: 'งวดเดิม', value: `${loans[target.key]?.installmentDays || 0} วัน` },
                        { label: 'ดอกใหม่', value: `${adjPreview.newRate}%` },
                        { label: 'ดอกเงิน', value: money(adjPreview.newInterest) },
                        { label: 'ยอดรวมใหม่', value: money(adjPreview.newTotal), highlight: true },
                      ]} />
                    </Col>
                    <Col xs={12} md={6}>
                      <SummaryTable title='สัญญาใหม่หลังปรับดอก' rows={[
                        { label: 'ฐานคำนวณ', value: money(adjPreview.remain), highlight: true },
                        { label: 'ยอดส่ง/วัน', value: money(adjPreview.newDaily), highlight: true },
                        { label: 'งวดใหม่', value: `${adjPreview.newDays} วัน` },
                      ]} />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm' onClick={closeModal} disabled={saving}>ยกเลิก</Button>
          <Button variant='warning' size='sm' disabled={saving} onClick={() => setConfirmShow(true)}>
            ยืนยันปรับดอก
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          Modal — ล้มยอด (2.2)
      ════════════════════════════════════════════════════════ */}
      <Modal show={modalMode === 'reset'} onHide={closeModal} fullscreen centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className='fa-solid fa-arrow-rotate-left me-2 text-success' />ล้มยอด — {target?.personal.fullName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {target && (
            <>
              <Row className='g-2 mb-3'>
                <Col xs={12} md={6} style={{overflowY:"scroll", height:"calc(100vh - 160px)"}}>
                  <Table size='sm' bordered style={{ fontSize: 12 }}>
                    <thead className='table-light'>
                      <tr>
                        <th>วันที่</th><th>ยอดที่ต้องจ่าย</th><th>จ่ายจริง</th><th>วันที่จ่าย</th><th>สถานะ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(installMap[target.key] || []).map((ins) => (
                        <tr
                          key={ins.seq}
                          style={{ backgroundColor: ins.seq === paySeq ? '#fffbf0' : undefined, cursor: 'pointer' }}
                          onClick={() => { setPaySeq(ins.seq); setPayAmount(String(ins.dueAmount)) }}
                        >
                          <td>{(ins as any).dueAt ? formatDateShort((ins as any).dueAt) : `งวด ${ins.seq}`}</td>
                          <td>{money(ins.dueAmount)}</td>
                          <td>{ins.paidAmount > 0 ? money(ins.paidAmount) : '-'}</td>
                          <td>{ins.paidAt ? formatDateShort(ins.paidAt) : '-'}</td>
                          <td>
                            <div className='d-flex align-items-center gap-1'>
                              <Badge bg={
                                ins.status === 'paid' ? 'success' :
                                  ins.status === 'partial' ? 'warning' :
                                    ins.status === 'skipped' ? 'secondary' : 'danger'
                              } style={{ fontSize: 10 }}>
                                {ins.status === 'paid' ? 'จ่ายแล้ว' : ins.status === 'partial' ? 'จ่ายบางส่วน' : ins.status === 'skipped' ? 'ข้าม' : 'รอชำระ'}
                              </Badge>
                              {(ins as any).slipId && (
                                <Button
                                  size='sm' variant='outline-primary'
                                  style={{ fontSize: 10, padding: '1px 5px' }}
                                  disabled={loadingSlip}
                                  onClick={(e) => { e.stopPropagation(); handleViewSlip((ins as any).slipId, ins.seq) }}
                                >
                                  <i className='fa-solid fa-image' />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
                <Col xs={12} md={6}>
                  <Row className='g-2 mb-3'>
                    <Col xs={12} md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>ยอดกู้ใหม่ (กีบ)</Form.Label>
                        <Form.Control size='sm' type='number' value={resetTopup} onChange={(e) => setResetTopup(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col xs={6} md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>ดอกเบี้ยใหม่ (%)</Form.Label>
                        <Form.Control size='sm' type='number' value={resetRate} onChange={(e) => setResetRate(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col xs={6} md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>จำนวนวันใหม่</Form.Label>
                        <Form.Control size='sm' type='number' value={resetDays} onChange={(e) => setResetDays(e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                  {resetPreview && (
                    <Row className='g-2'>
                      <Col xs={12} md={6}>
                        <SummaryTable title='สัญญาเดิม' rows={[
                          { label: 'ยอดคงเหลือเดิม', value: money(resetPreview.remain), highlight: true },
                          { label: 'จ่ายมาแล้ว', value: money(resetPreview.paid) },
                          { label: 'ดอก', value: `${resetPreview.newRate}%  (+${money(resetPreview.newInterest)})` },
                          { label: 'ยอดรวมใหม่', value: money(resetPreview.newTotal), highlight: true },
                        ]} />
                      </Col>
                      <Col xs={12} md={6}>
                        <SummaryTable title='สัญญาใหม่หลังล้มยอด' rows={[
                          { label: 'ยอดกู้ใหม่', value: money(resetPreview.topup), highlight: true },
                          { label: 'ยอดส่ง/วัน', value: money(resetPreview.newDaily), highlight: true },
                          { label: 'โอนให้ลูกค้า', value: money(resetPreview.refund), highlight: resetPreview.refund > 0 },
                        ]} />
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>

            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm' onClick={closeModal} disabled={saving}>ยกเลิก</Button>
          <Button variant='success' size='sm' disabled={saving} onClick={() => setConfirmShow(true)}>
            ยืนยันล้มยอด
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          Modal — ล้มจบหนี้สิน (ปิดบัญชี → data_d)
      ════════════════════════════════════════════════════════ */}
      <Modal show={modalMode === 'settle'} onHide={closeModal} size='lg' centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className='fa-solid fa-flag-checkered me-2 text-success' />ล้มจบหนี้สิน — {target?.personal.fullName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {target && settlePreview && (
            <>
              <Alert variant={settlePreview.eligible ? 'success' : 'warning'} style={{ fontSize: 13 }}>
                {settlePreview.eligible
                  ? 'ยอดคงเหลืออยู่ในเกณฑ์ (≤ 50% ของยอดรวม) — เลือกการดำเนินการด้านล่าง'
                  : 'ยังล้มจบหนี้สินไม่ได้ — ต้องชำระมาแล้วอย่างน้อยครึ่งหนึ่ง (ยอดคงเหลือ ≤ 50% ของยอดรวม)'}
              </Alert>
              <SummaryTable title='สรุปยอดสัญญาเดิม' rows={[
                { label: 'ยอดรวมทั้งสัญญา', value: money(settlePreview.total) },
                { label: 'ชำระมาแล้ว', value: money(settlePreview.paid) },
                { label: 'ยอดคงเหลือ', value: `${money(settlePreview.remain)} (${settlePreview.pctRemain.toFixed(1)}%)`, highlight: true },
                { label: 'เกณฑ์ล้มจบ (50% ของยอดรวม)', value: `≤ ${money(settlePreview.threshold)}` },
              ]} />

              {settlePreview.eligible && (
                <>
                  <div style={{ fontWeight: 700, fontSize: 13, marginTop: 16, marginBottom: 8 }}>เลือกการดำเนินการ</div>
                  <div className='d-flex gap-2'>
                    <Button
                      variant={settleMode === 'close' ? 'success' : 'outline-success'}
                      size='sm' className='flex-fill'
                      onClick={() => setSettleMode('close')}
                    >
                      <i className='fa-solid fa-flag-checkered me-1' />ปิดบัญชี (จบหนี้)
                    </Button>
                    <Button
                      variant={settleMode === 'renew' ? 'primary' : 'outline-primary'}
                      size='sm' className='flex-fill'
                      onClick={() => setSettleMode('renew')}
                    >
                      <i className='fa-solid fa-arrow-rotate-left me-1' />เริ่มกู้ใหม่
                    </Button>
                  </div>

                  {settleMode === 'close' && (
                    <div className='text-muted mt-3' style={{ fontSize: 12 }}>
                      ย้ายลูกค้าไปยัง <strong>data_d (ปิดบัญชี)</strong> — เก็บข้อมูลสินเชื่อ/งวดไว้ดูย้อนหลัง ไม่สร้างสัญญาใหม่
                    </div>
                  )}

                  {settleMode === 'renew' && (
                    <div className='mt-3'>
                      <Row className='g-2 mb-2'>
                        <Col xs={12} md={4}>
                          <Form.Group>
                            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>ยอดกู้ใหม่ (กีบ)</Form.Label>
                            <Form.Control size='sm' type='number' value={settleTopup} onChange={(e) => setSettleTopup(e.target.value)} />
                          </Form.Group>
                        </Col>
                        <Col xs={6} md={4}>
                          <Form.Group>
                            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>ดอกเบี้ยใหม่ (%)</Form.Label>
                            <Form.Control size='sm' type='number' value={settleRate} onChange={(e) => setSettleRate(e.target.value)} />
                          </Form.Group>
                        </Col>
                        <Col xs={6} md={4}>
                          <Form.Group>
                            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>จำนวนวันใหม่</Form.Label>
                            <Form.Control size='sm' type='number' value={settleDays} onChange={(e) => setSettleDays(e.target.value)} />
                          </Form.Group>
                        </Col>
                      </Row>
                      {settleRenewPreview && (
                        <SummaryTable title='สัญญาใหม่ (ยอดใหม่ทั้งหมด)' rows={[
                          { label: 'ยอดกู้ใหม่', value: money(settleRenewPreview.topup) },
                          { label: 'ดอก', value: `${settleRenewPreview.rate}%  (+${money(settleRenewPreview.interestAmount)})` },
                          { label: 'ยอดรวมใหม่', value: money(settleRenewPreview.totalWithInterest), highlight: true },
                          { label: 'ยอดส่ง/วัน', value: money(settleRenewPreview.dailyPayment), highlight: true },
                          { label: 'จำนวนงวด', value: `${settleRenewPreview.days} วัน` },
                        ]} />
                      )}
                      <div className='text-muted mt-2' style={{ fontSize: 12 }}>
                        สัญญาเดิมจะถูกปิดและย้ายไป <strong>data_d</strong> (เก็บหลักฐาน) จากนั้นสร้างสัญญาใหม่ใน <strong>data_c</strong> ด้วย key ใหม่ (หักงวดแรกทันที) — รูปภาพ/เอกสารเดิมจะยังอยู่กับสัญญาเดิมใน data_d
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm' onClick={closeModal} disabled={saving}>ยกเลิก</Button>
          <Button
            variant={settleMode === 'renew' ? 'primary' : 'success'}
            size='sm'
            disabled={saving || !settlePreview?.eligible || (settleMode === 'renew' && !settleRenewPreview)}
            onClick={() => setConfirmShow(true)}
          >
            {saving ? 'กำลังบันทึก...' : settleMode === 'renew' ? 'ยืนยันล้มจบ + กู้ใหม่' : 'ยืนยันปิดบัญชี'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          Modal — หนี้เสีย (2.3)
      ════════════════════════════════════════════════════════ */}
      <Modal show={modalMode === 'badDebt'} onHide={closeModal} centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className='fa-solid fa-triangle-exclamation me-2 text-danger' />หนี้เสีย — {target?.personal.fullName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant='danger' style={{ fontSize: 13 }}>
            การดำเนินการนี้จะย้ายข้อมูลไปยัง data_e (หนี้เสีย) ทันที และไม่สามารถย้อนกลับได้
          </Alert>
          {target && loans[target.key] && (
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              <div>คงเหลือ: <strong>{money(calcRemain(loans[target.key], installMap[target.key] || []))}</strong> กีบ</div>
            </div>
          )}
          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>เหตุผล</Form.Label>
            <Form.Control
              as='textarea' rows={2} size='sm'
              value={badReason}
              onChange={(e) => setBadReason(e.target.value)}
              placeholder='ระบุเหตุผล...'
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm' onClick={closeModal} disabled={saving}>ยกเลิก</Button>
          <Button variant='danger' size='sm' disabled={saving} onClick={() => setConfirmShow(true)}>
            ยืนยันหนี้เสีย
          </Button>
        </Modal.Footer>
      </Modal>
      {/* ════════════════════════════════════════════════════════
          Modal — ดูสลิป
      ════════════════════════════════════════════════════════ */}
      <Modal show={!!viewSlip} onHide={() => setViewSlip(null)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className='fa-solid fa-image me-2' />
            สลิปงวดที่ {viewSlip?.seq} — {target?.personal.fullName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center', background: '#111', padding: 8 }}>
          {viewSlip && (
            <img
              src={viewSlip.image}
              alt={viewSlip.name}
              style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <span style={{ fontSize: 12, color: '#888', marginRight: 'auto' }}>{viewSlip?.name}</span>
          <Button size='sm' variant='outline-secondary' onClick={() => setViewSlip(null)}>ปิด</Button>
        </Modal.Footer>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          Modal — ตรวจรับเงินภาคสนาม
      ════════════════════════════════════════════════════════ */}
      <ReviewFieldModal
        show={showReview}
        reg={reviewReg}
        receipts={reviewReg ? recentPending(reviewReg.key) : []}
        installments={reviewReg ? (installMap[reviewReg.key] || []) : []}
        remain={reviewReg && loans[reviewReg.key] ? calcRemain(loans[reviewReg.key], installMap[reviewReg.key] || []) : 0}
        saving={reviewSaving}
        onApply={(receipt, seq, amount) => reviewReg && confirmReceipt(reviewReg, receipt, seq, amount)}
        onClose={() => { setShowReview(false); setReviewReg(null) }}
      />

      {/* ════════════════════════════════════════════════════════
          Modal — ข้อมูลเอกสาร
      ════════════════════════════════════════════════════════ */}
      <Modal show={docModal} onHide={() => !docGenerating && setDocModal(false)} centered>
        <Modal.Header closeButton={!docGenerating}>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className='fa-solid fa-file-excel me-2 text-success' />ข้อมูลเอกสาร — ดาวน์โหลดสายเก็บเงิน
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 13, marginBottom: 16, color: '#666' }}>
            สร้างไฟล์ Excel สายเก็บเงินจากข้อมูลปัจจุบัน พร้อม format สำหรับ print
          </div>
          <Form.Group className='mb-3'>
            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>จำนวนวันที่แสดง</Form.Label>
            <Form.Select size='sm' value={docDays}
              onChange={(e) => setDocDays(Number(e.target.value))}>
              <option value={7}>7 วัน</option>
              <option value={14}>14 วัน</option>
              <option value={17}>17 วัน (เหมือนต้นฉบับ)</option>
              <option value={30}>30 วัน</option>
            </Form.Select>
          </Form.Group>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>เลือกโซนที่ต้องการ:</div>
          <div className='d-flex flex-column gap-2'>
            {(['A', 'B', 'C'] as const).map((z) => {
              const cnt = customers.filter((c) => {
                const l = loans[c.key]
                const zz = l?.zone || ''
                const zArr = Array.isArray(zz) ? zz : [zz]
                return zArr.some((x: string) => x.trim().toUpperCase() === z)
              }).length
              return (
                <Button
                  key={z}
                  variant='outline-success'
                  disabled={docGenerating || cnt === 0}
                  onClick={() => handleGenerateDoc(z)}
                  style={{ textAlign: 'left', justifyContent: 'space-between', display: 'flex' }}
                >
                  <span>
                    <i className='fa-solid fa-file-excel me-2' />
                    ดาวน์โหลด สาย{z}
                  </span>
                  <span style={{ fontSize: 11, opacity: 0.7 }}>{cnt} ลูกค้า</span>
                </Button>
              )
            })}
          </div>
          {docGenerating && (
            <div className='d-flex align-items-center gap-2 mt-3' style={{ fontSize: 13, color: '#666' }}>
              <span className='spinner-border spinner-border-sm' />
              กำลังสร้างไฟล์...
            </div>
          )}

          <hr className='my-3' />

          <div style={{ fontSize: 13, marginBottom: 10, color: '#666' }}>
            <i className='fa-solid fa-table-list me-2' />
            สรุปยอดที่ต้องเก็บประจำวัน — แยกสีตามสถานะ (ปรกติ / ค้าง / ค้างสะสม / ปิดยอด)
          </div>
          <div className='d-flex align-items-end gap-2'>
            <Form.Group style={{ flex: 1 }}>
              <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>เลือกวันที่</Form.Label>
              <Form.Control
                type='date' size='sm'
                value={summaryDate}
                onChange={(e) => setSummaryDate(e.target.value)}
              />
            </Form.Group>
            <Button
              variant='outline-primary'
              disabled={summaryGenerating}
              onClick={handleGenerateSummary}
            >
              {summaryGenerating
                ? <><span className='spinner-border spinner-border-sm me-2' />กำลังสร้าง...</>
                : <><i className='fa-solid fa-file-excel me-2' />สรุปยอดที่ต้องเก็บ</>
              }
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm'
            onClick={() => setDocModal(false)} disabled={docGenerating}>
            ปิด
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}